import type { Adjustment, Cents, EbitdaBridge, EvidenceSource, ExtractedValue } from "./types";

/** Signed contribution of one adjustment to the bridge. */
export function adjustmentDelta(adjustment: Adjustment): Cents {
  return adjustment.direction === "add" ? adjustment.amount : -adjustment.amount;
}

/** Reported EBITDA plus every analyst adjustment. The number the decision uses. */
export function adjustedEbitda(bridge: EbitdaBridge): Cents {
  return bridge.adjustments.reduce((total, a) => total + adjustmentDelta(a), bridge.reported);
}

/** Every extracted value across all sources, flattened. */
export function allExtracted(sources: EvidenceSource[]): ExtractedValue[] {
  return sources.flatMap((source) => source.extracted);
}

/** Values an underwriter still has to deal with before a decision is defensible. */
export function openExceptions(sources: EvidenceSource[]): ExtractedValue[] {
  return allExtracted(sources).filter((value) => value.status !== "verified");
}

/** Share of extracted values that have been confirmed, 0–1. */
export function verifiedShare(sources: EvidenceSource[]): number {
  const values = allExtracted(sources);
  if (values.length === 0) return 0;
  return values.filter((value) => value.status === "verified").length / values.length;
}

/** Confidence bands. Below 0.75 an extraction is not trustworthy on its own. */
export function confidenceBand(confidence: number): "high" | "medium" | "low" {
  if (confidence >= 0.9) return "high";
  if (confidence >= 0.75) return "medium";
  return "low";
}
